<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import draggable from 'vuedraggable'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { RouterLinkBack, VButton } from '@/modules/shared/components'
import ApplicationAddQuestionButton from '../components/application-add-question-button.vue'
import ApplicationHeadingNav from '../components/application-heading-nav.vue'
import ApplicationQuestionCard from '../components/application-question-card.vue'
import ApplicationStateBadge from '../components/application-state-badge.vue'
import { useApplicationStore } from '../stores/application-store'
import { useBuilderStore } from '../stores/builder-store'
import type { ApplicationQuestion } from '../types'

const route = useRoute()
const router = useRouter()
const builderStore = useBuilderStore()
const applicationStore = useApplicationStore()

const setId = computed(() => Number(route.params.setId))
const template = computed(() => builderStore.template)
const headings = computed(() => template.value?.headings ?? [])

const selectedHeadingId = ref<number | null>(null)
const selectedHeading = computed(() => headings.value.find((h) => h.id === selectedHeadingId.value) ?? null)

// the freshly created question gets focus in its name field, keeping the user
// where they were instead of losing their place after the refetch
const lastCreatedQuestionId = ref<number | null>(null)

const onQuestionCreated = (question: ApplicationQuestion) => {
  lastCreatedQuestionId.value = question.id
}

const selectHeading = (headingId: number) => {
  selectedHeadingId.value = headingId
  // switching sections abandons the pending focus — a stale id would steal
  // focus back to the wrong heading later
  lastCreatedQuestionId.value = null
}

const selectFirstHeading = () => {
  selectedHeadingId.value = headings.value[0]?.id ?? null
  lastCreatedQuestionId.value = null
}

// a refetch can drop the selected heading (deleted in the nav, or replaced by
// a duplicate) — fall back to the first one instead of a dead selection
watch(headings, () => {
  if (selectedHeading.value === null && headings.value.length > 0) selectFirstHeading()
})

watch(setId, () => {
  builderStore.fetchTemplate(setId.value).then(selectFirstHeading)
})

onMounted(() => {
  builderStore.fetchTemplate(setId.value).then(selectFirstHeading)
})

onBeforeRouteLeave(() => {
  builderStore.reset()
})

///////////////////////////////////////////////////////////////////////////////
// Template header — inline rename + lifecycle actions
///////////////////////////////////////////////////////////////////////////////

const editingName = ref(false)
const nameValue = ref('')
const savingName = ref(false)

const startRename = () => {
  nameValue.value = template.value?.name ?? ''
  editingName.value = true
}

const saveRename = async () => {
  const name = nameValue.value.trim()
  if (!name || !template.value || savingName.value) return

  savingName.value = true
  try {
    await builderStore.renameTemplate(name)
    editingName.value = false
  } catch {
    // error already surfaced via { silent: false }
  } finally {
    savingName.value = false
  }
}

// guards double-clicks and swallows rejections (the error toast already
// surfaced via { silent: false })
const lifecycleRunning = ref(false)
const runLifecycle = async (action: () => Promise<void>) => {
  if (lifecycleRunning.value) return

  lifecycleRunning.value = true
  try {
    await action()
  } catch {
    // error already surfaced via { silent: false }
  } finally {
    lifecycleRunning.value = false
  }
}

const duplicate = () =>
  runLifecycle(async () => {
    if (!template.value) return

    const copy = await applicationStore.duplicateTemplate(template.value.id)
    router.push({ name: 'applications.builder', params: { setId: copy.id } })
  })

const remove = () =>
  runLifecycle(async () => {
    if (!template.value) return
    if (!window.confirm(`Delete "${template.value.name}"? This cannot be undone.`)) return

    await applicationStore.deleteTemplate(template.value.id)
    router.push({ name: 'applications.list' })
  })

///////////////////////////////////////////////////////////////////////////////
// Question sorting within the selected heading
///////////////////////////////////////////////////////////////////////////////

const localQuestions = ref<ApplicationQuestion[]>([])

watch(
  () => selectedHeading.value?.questions,
  (next) => {
    localQuestions.value = [...(next ?? [])]
  },
  { immediate: true },
)

const onDragEnd = () => {
  if (!selectedHeading.value) return

  const draggedIds = localQuestions.value.map((q) => q.id)
  const serverIds = selectedHeading.value.questions.map((q) => q.id)
  // dropping a question back in place still fires onEnd — skip the no-op POST
  if (draggedIds.every((id, index) => id === serverIds[index])) return

  builderStore
    .sortQuestions(selectedHeading.value.id, draggedIds)
    .catch(() => undefined /* error already surfaced via { silent: false } */)
}
</script>

<template>
  <TheLayout>
    <div class="flex flex-col gap-4">
      <RouterLinkBack :to="{ name: 'applications.list' }">
        <VButton variant="secondary" size="xs">← Back to Applications</VButton>
      </RouterLinkBack>

      <p v-if="builderStore.loading && !template" class="py-16 text-center text-sm text-gray-500">
        Loading application…
      </p>

      <div
        v-else-if="builderStore.notFound || !template"
        class="rounded-xl border border-gray-200 bg-white py-16 text-center shadow-sm"
      >
        <p class="text-sm text-gray-500">Application not found.</p>
      </div>

      <div
        v-else-if="builderStore.error"
        class="rounded-xl border border-gray-200 bg-white py-16 text-center shadow-sm"
      >
        <p class="text-sm text-gray-500">Failed to load the application. Please try again.</p>
      </div>

      <template v-else>
        <header class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-3">
            <template v-if="editingName">
              <input
                v-model="nameValue"
                type="text"
                class="w-72 rounded-lg border border-gray-200 px-3 py-1.5 text-xl font-bold outline-none focus:border-[#3b88af]"
                @keyup.enter="saveRename"
                @keyup.esc="editingName = false"
              />
              <VButton variant="v-blue" size="xs" :loading="savingName" @click="saveRename">Save</VButton>
            </template>
            <template v-else>
              <h1 class="text-2xl font-bold text-gray-900">{{ template.name }}</h1>
              <button class="text-sm text-gray-400 hover:text-gray-600" title="Rename application" @click="startRename">
                Edit
              </button>
            </template>
            <ApplicationStateBadge :state="template.state" />
          </div>

          <div class="flex items-center gap-2">
            <VButton
              v-if="template.state === 'draft'"
              variant="secondary"
              size="xs"
              :loading="lifecycleRunning"
              @click="runLifecycle(() => builderStore.publish())"
            >
              Publish
            </VButton>
            <VButton
              v-if="template.state === 'published'"
              variant="secondary"
              size="xs"
              :loading="lifecycleRunning"
              @click="runLifecycle(() => builderStore.draft())"
            >
              Draft
            </VButton>
            <VButton
              v-if="template.state !== 'archived'"
              variant="secondary"
              size="xs"
              :loading="lifecycleRunning"
              @click="runLifecycle(() => builderStore.archive())"
            >
              Archive
            </VButton>
            <VButton variant="secondary" size="xs" :loading="lifecycleRunning" @click="duplicate">Duplicate</VButton>
            <VButton variant="v-red" size="xs" :loading="lifecycleRunning" @click="remove">Delete</VButton>
          </div>
        </header>

        <div class="grid grid-cols-12 gap-6">
          <!-- sections — the 'Headings' title + create affordance live in the
               nav card header, coordinated with its editor state -->
          <aside class="col-span-4 lg:col-span-3">
            <ApplicationHeadingNav
              :headings="headings"
              :selectedHeadingId="selectedHeadingId"
              @select="selectHeading"
            />
          </aside>

          <!-- questions -->
          <main class="col-span-8 lg:col-span-9">
            <template v-if="selectedHeading">
              <div class="mb-4">
                <h2 class="text-xl font-bold text-gray-900">{{ selectedHeading.name }}</h2>
                <p v-if="selectedHeading.description" class="mt-1 whitespace-pre-wrap text-sm text-gray-600">
                  {{ selectedHeading.description }}
                </p>
              </div>

              <!-- legacy pattern: the create-question button sits above the
                   list and after every question; each inserts at its own spot
                   (top button first, a button after question N at N+1) -->
              <ApplicationAddQuestionButton
                :headingId="selectedHeading.id"
                :position="1"
                @created="onQuestionCreated"
              />

              <div v-if="localQuestions.length === 0" class="py-12 text-center">
                <p class="text-sm font-medium text-gray-700">No questions to display</p>
                <p class="mt-1 text-sm text-gray-500">Create one by clicking the plus sign above.</p>
              </div>

              <draggable
                v-else
                v-model="localQuestions"
                item-key="id"
                handle=".drag-handle"
                :animation="150"
                class="flex flex-col gap-4"
                @end="onDragEnd"
              >
                <template #item="{ element }">
                  <div class="group relative">
                    <!-- Stable grab cursor: fixed-size flex hit box, no text
                         selection, pointer events only while revealed -->
                    <span
                      class="drag-handle pointer-events-none absolute -left-6 top-1/2 flex h-6 w-4 -translate-y-1/2 cursor-grab select-none items-center justify-center text-gray-300 opacity-0 transition-opacity active:cursor-grabbing group-hover:pointer-events-auto group-hover:opacity-100"
                      title="Drag to reorder"
                      >⋮⋮</span
                    >
                    <ApplicationQuestionCard
                      :question="element"
                      :autofocus="element.id === lastCreatedQuestionId"
                      @focused="lastCreatedQuestionId = null"
                    />
                    <ApplicationAddQuestionButton
                      :headingId="selectedHeading.id"
                      :position="element.position + 1"
                      @created="onQuestionCreated"
                    />
                  </div>
                </template>
              </draggable>
            </template>

            <div v-else class="rounded-xl border border-gray-200 bg-white py-12 text-center shadow-sm">
              <p class="text-sm text-gray-500">Add a heading to start building questions.</p>
            </div>
          </main>
        </div>
      </template>
    </div>
  </TheLayout>
</template>
